import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Text, Icon, Radio } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { PlatformType } from '../../types/project';
import type { ProjectStacksState } from '../../hooks/useProjectStacks';
import { fetchCustomInstructions, refusalMessage } from '../../services/customInstructions';
import {
  CloneSource,
  DrawerWidth,
  SOURCE_STACK,
  StackChoice,
  StackPicks,
  canCloneStack,
  clonePresets,
  cloneStackFields,
  cloneStackSection,
  closesOnBackdrop,
  drawerWidth,
  keepsSourceCode,
  listedCloneChoice,
  sourceCodeLine,
  withSourceInstructions,
} from '../../utils/stackPicker';
import { StackNameInFooter, TechStackField } from './TechStackField';

/** The Clone drawer's input, with the Tech Stack chosen for a Web clone
 *  (StackChoice): SOURCE_STACK for "Same as the source project", a preset's id or
 *  the Custom option, with its picks, None in every group the first time it is
 *  chosen, and the Special Instructions box's text, prefilled once from the
 *  source's. */
export interface UserInput extends StackChoice {
  title?: string;
  clientName?: string;
  projectType?: number;
  platformType?: PlatformType;
}

/** What "Same as the source project" says of the source's special instructions. */
const INSTRUCTIONS_COPIED = 'Special instructions are copied too.';

/** The source's special instructions, read for the box once another stack is chosen. */
type SourceInstructions =
  | { kind: 'idle' | 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; text: string };

interface CloneProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  /** Why the clone couldn't be started: the platform's plain words for a 400 or a 503, else a plain message. */
  error: string;
  userInput: UserInput;
  setUserInput: (v: UserInput) => void;
  /** The tech stacks GET /projects/stacks offers, as the dashboard read them when
   *  it loaded and keeps them, so the drawer knows its width as it opens, unless
   *  it opens before they are read (drawerWidth). */
  stacks: ProjectStacksState;
  /** Reads them again. */
  reloadStacks: () => void;
  /** The source project's id, whose special instructions the box is prefilled from. */
  sourceId?: string;
  sourcePlatformType?: PlatformType;
  /** The source project's type: 1 for a Web project, 2 for a Low Code one. */
  sourceProjectType?: number;
  /** Its prompt version as stored: null for a project older than prompt versions. */
  sourcePromptVersion?: number | null;
  /** The label of its tech stack, which "Same as the source project" keeps: null for one that can't be read. */
  sourceStackLabel?: string | null;
  /** Its picks (group -> part id): null for a Low Code project and a stack that can't be read. */
  sourceStackPicks?: StackPicks | null;
  /** Whether it has special instructions, which "Same as the source project" copies. */
  sourceHasCustomInstructions?: boolean;
}

/**
 * The Clone drawer (multi-stack step 3.14h, layout A of the owner's pick,
 * 2026-10-20; the rule is step 3.14f's). A Web clone takes Create Project's Tech
 * Stack list (TechStackField) with "Same as the source project" first, described
 * by the source's label and preselected, and without the preset on the source's
 * own picks; a Web clone of a Low Code project takes the list as Create does,
 * the default preselected. Under the list a line says whether the source code is
 * copied, as the clone API decides it (keepsSourceCode). "Same as the source
 * project" copies the source's special instructions, and any other choice gets
 * the box, prefilled once from them; while the Custom option, and so the box,
 * isn't offered, a source that has some has "Same as the source project" alone.
 * A list of one choice shows it as text, as before the step, a Web source's
 * only with its label, and while the Custom option isn't offered a source whose
 * stack can't be read has "Same as the source project" alone, so nothing of its
 * stack shows, as then (cloneStackSection, multi-stack step 3.14j). A clone to
 * Low Code shows no stack and no box. The drawer is 720px wide where the Custom
 * option is offered, as Create Project is, and the clone API's refusals show at
 * the end of the form.
 */
export const CloneProjectModal: React.FC<CloneProjectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  error,
  userInput,
  setUserInput,
  stacks,
  reloadStacks,
  sourceId,
  sourcePlatformType,
  sourceProjectType,
  sourcePromptVersion,
  sourceStackLabel,
  sourceStackPicks,
  sourceHasCustomInstructions,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const errorRef = useRef<HTMLElement>(null);

  const presets = stacks.kind === 'loaded' ? stacks.presets : [];
  const custom = stacks.kind === 'loaded' ? stacks.custom : null;
  const source = useMemo<CloneSource>(
    () => ({
      projectType: sourceProjectType,
      promptVersion: sourcePromptVersion,
      label: sourceStackLabel,
      picks: sourceStackPicks,
      hasCustomInstructions: sourceHasCustomInstructions,
    }),
    [sourceProjectType, sourcePromptVersion, sourceStackLabel, sourceStackPicks, sourceHasCustomInstructions],
  );
  const sourceIsWeb = (sourceProjectType ?? 1) === 1;
  const isWeb = userInput.projectType === 1;

  // The drawer's width, decided as Create Project's is (drawerWidth): 720px,
  // capped at the screen, where the list offers the Custom option, else 400px,
  // as the drawer opens from the dashboard's copy of the list or, for a drawer
  // opened before that copy is read, when the first list lands while it is open,
  // its Tech Stack list showing with it; then kept until it closes, however the
  // list is read again, and while it slides out, and decided again the next time
  // it opens.
  const [width, setWidth] = useState<DrawerWidth | null>(null);
  const nextWidth = drawerWidth(width, stacks.kind === 'loaded' ? stacks : null, isOpen, isClosing);
  if (nextWidth !== width) setWidth(nextWidth);
  const wide = nextWidth === 720;

  // The list is the dashboard's copy, read again each time the drawer opens and
  // after a clone failed to start, as Create Project reads it.
  useEffect(() => {
    if (isOpen) reloadStacks();
  }, [isOpen, reloadStacks]);

  useEffect(() => {
    if (!isOpen || !error) return;
    reloadStacks();
    errorRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, error, reloadStacks]);

  // The source's special instructions, read once as the drawer opens, where the
  // box can take them: a Web source on prompt version 2 that has some, while the
  // Custom option is offered. They prefill the box the first time it shows.
  // While it isn't, such a source has "Same as the source project" alone, which
  // copies them (cloneChoices).
  const readsSourceInstructions =
    isOpen && sourceIsWeb && sourcePromptVersion === 2 && !!sourceHasCustomInstructions && !!custom && !!sourceId;
  const [sourceInstructions, setSourceInstructions] = useState<SourceInstructions>({ kind: 'idle' });
  // Drops a reply that a later read overtook, or that comes after the drawer closed.
  const readSeq = useRef(0);
  const loadSourceInstructions = useCallback(async () => {
    if (!sourceId) return;
    const id = ++readSeq.current;
    setSourceInstructions({ kind: 'loading' });
    try {
      const instructions = await fetchCustomInstructions(sourceId);
      if (id === readSeq.current) setSourceInstructions({ kind: 'loaded', text: instructions.text });
    } catch (err) {
      if (id === readSeq.current) {
        setSourceInstructions({ kind: 'error', message: refusalMessage(err) ?? "Special instructions couldn't be loaded." });
      }
    }
  }, [sourceId]);
  useEffect(() => {
    if (isOpen) return;
    readSeq.current++;
    setSourceInstructions({ kind: 'idle' });
  }, [isOpen]);
  useEffect(() => {
    if (readsSourceInstructions && sourceInstructions.kind === 'idle') loadSourceInstructions();
  }, [readsSourceInstructions, sourceInstructions.kind, loadSourceInstructions]);

  // The choice as the drawer shows it: the box prefilled once its text is read.
  const loadedInstructions = sourceInstructions.kind === 'loaded' && !!custom && isWeb ? sourceInstructions.text : null;
  const choice = loadedInstructions !== null ? withSourceInstructions(userInput, loadedInstructions) : userInput;

  // A Web clone carries the choice the list shows: "Same as the source project"
  // for a Web source until the user picks another, the default for a Low Code
  // source, Custom's picks and the box's text only as far as the list allows
  // them, and the box's text once prefilled.
  useEffect(() => {
    if (!isOpen) return;
    let next = stacks.kind === 'loaded' ? listedCloneChoice(source, stacks.presets, stacks.custom, userInput) : userInput;
    if (loadedInstructions !== null) next = withSourceInstructions(next, loadedInstructions);
    if (next !== userInput) setUserInput(next);
  }, [isOpen, stacks, source, userInput, setUserInput, loadedInstructions]);

  const sameChosen = choice.stack === SOURCE_STACK;
  // The box waits for the source's text, never shown empty in its place.
  const waitingForInstructions =
    readsSourceInstructions && sourceInstructions.kind !== 'loaded' && userInput.customInstructions === undefined;

  // More than one choice shows the list; one, or a Web source's while the list
  // isn't read, shows as text, for a Web source only with its label: one whose
  // stack can't be read shows nothing there, as before step 3.14h (cloneStackSection).
  const techStack = cloneStackSection(source, stacks.kind === 'loaded' ? stacks : null);

  // The stack's name in the footer while the Custom option is chosen, as in Create Project.
  const stackNameInFooter = !!custom && isWeb;
  // While the Custom option is offered, a Web clone's tech stack is one the clone
  // API takes, as Create Project's is, and the box holds the source's text before
  // another stack can be sent.
  const stackApplies =
    !isWeb || !custom || (canCloneStack(custom, choice) && (sameChosen || !waitingForInstructions));
  const isSubmitDisabled = loading || !userInput.title?.trim() || !stackApplies;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(e);
  };

  if (!isOpen && !isClosing) return null;

  const instructionsCopied = sourceIsWeb && !!sourceHasCustomInstructions;

  // Under the list: whether the source code is copied, then, for "Same as the
  // source project", that the special instructions are too, or, for another
  // choice, the source's text being read in place of the box.
  const belowList = (
    <div className="mt-bg-v2-lg flex flex-col gap-bg-v2-xs">
      <Text variant="text-sm" color="tertiary">{sourceCodeLine(keepsSourceCode(source, choice, presets))}</Text>
      {sameChosen ? instructionsCopied && (
        <Text variant="text-sm" color="tertiary">{INSTRUCTIONS_COPIED}</Text>
      ) : waitingForInstructions && (
        sourceInstructions.kind === 'error' ? (
          <div className="flex items-center gap-bg-v2-md">
            <Text variant="text-sm" color="error" as="span">{sourceInstructions.message}</Text>
            <Button type="button" variant="link" size="small" label="Try again" onClick={loadSourceInstructions} />
          </div>
        ) : (
          <Text variant="text-sm" color="tertiary">Loading special instructions…</Text>
        )
      )}
    </div>
  );

  // A click on the backdrop leaves the drawer open while what Clone Project would
  // send holds Custom's picks or special instructions (closesOnBackdrop): not for
  // the box's text, typed or prefilled, once "Same as the source project" hides
  // the box or Low Code is chosen. Cancel and the close button close it.
  return (
    <div 
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} 
      onClick={closesOnBackdrop(custom, choice.projectType, cloneStackFields(choice)) ? handleClose : undefined}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary ${wide ? 'w-[720px] max-w-full' : 'w-[400px]'} flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Top Section */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <div>
            <Text variant="text-xl" weight="bold">Clone Project</Text>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
            disabled={loading}
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        {/* The form takes the height left and its middle scrolls, so the footer
            never drops below the screen, as in Create Project. */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Middle Section */}
          <div className={`flex flex-col items-center gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto min-h-0 ${wide ? 'flex-1' : ''}`}>
            <div className="w-full space-y-6">
              <Input
                id="project-name-clone"
                name="project-name-clone"
                label="Project Name"
                value={userInput.title || ''}
                onChange={e => setUserInput({ ...userInput, title: e.target.value })}
                required
                autoFocus
                placeholder="Enter project name"
                className="w-full"
              />

              <div className="h-[1px] bg-border-secondary w-full"></div>

              <div className="w-full">
                <Text variant="text-md" weight="semibold" color="primary" className="mb-3">Project Type</Text>
                <div className="space-y-2">
                  <Radio
                    name="cloneProjectType"
                    value={1}
                    checked={userInput.projectType === 1}
                    onChange={() => setUserInput({ ...userInput, projectType: 1, platformType: undefined })}
                    label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">Web Application</Text>}
                    size='sm'
                  />
                  <Radio
                    name="cloneProjectType"
                    value={2}
                    checked={userInput.projectType === 2}
                    onChange={() => setUserInput({ ...userInput, projectType: 2, platformType: sourcePlatformType })}
                    label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">Low Code Application</Text>}
                    size='sm'
                  />
                </div>
              </div>

              {userInput.projectType === 2 ? (
                <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '0.08s', animationFillMode: 'forwards' }}>
                  <Text variant="text-md" weight="semibold" color="primary" className="mb-3">Platform Type</Text>
                  <div className="space-y-2">
                    <Radio
                      name="clonePlatformType"
                      value={1}
                      checked={userInput.platformType === PlatformType.TylerAppPlatform}
                      onChange={() => setUserInput({ ...userInput, platformType: PlatformType.TylerAppPlatform })}
                      label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">Tyler Application Platform (TAP)</Text>}
                      size='sm'
                    />
                    <Radio
                      name="clonePlatformType"
                      value={2}
                      checked={userInput.platformType === PlatformType.ServiceNow}
                      onChange={() => setUserInput({ ...userInput, platformType: PlatformType.ServiceNow })}
                      label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">ServiceNow (SN)</Text>}
                      size='sm'
                    />
                    <Radio
                      name="clonePlatformType"
                      value={3}
                      checked={userInput.platformType === PlatformType.PowerApps}
                      onChange={() => setUserInput({ ...userInput, platformType: PlatformType.PowerApps })}
                      label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">PowerApps (PA)</Text>}
                      size='sm'
                    />
                    <Radio
                      name="clonePlatformType"
                      value={4}
                      checked={userInput.platformType === PlatformType.Salesforce}
                      onChange={() => setUserInput({ ...userInput, platformType: PlatformType.Salesforce })}
                      label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">Salesforce (SF)</Text>}
                      size='sm'
                    />
                  </div>
                </div>
              ) : techStack.kind === 'list' ? (
                <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '0.08s', animationFillMode: 'forwards' }}>
                  <TechStackField
                    name="cloneTechStack"
                    stacks={stacks}
                    reloadStacks={reloadStacks}
                    choice={choice}
                    onChange={setUserInput}
                    leadingOption={sourceIsWeb ? {
                      value: SOURCE_STACK,
                      label: 'Same as the source project',
                      description: sourceStackLabel ?? undefined,
                    } : undefined}
                    presets={clonePresets(source, presets)}
                    belowList={stacks.kind === 'loaded' ? belowList : undefined}
                    showBox={!sameChosen && !waitingForInstructions}
                    boxId="clone-project-instructions"
                  />
                </div>
              ) : techStack.kind === 'text' ? (
                <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '0.08s', animationFillMode: 'forwards' }}>
                  <Text variant="text-md" weight="semibold" color="primary">Tech Stack</Text>
                  <Text variant="text-sm" color="tertiary" className="mb-bg-v2-lg">{techStack.note}</Text>
                  <Text variant="text-sm" weight="semibold" color="tertiary">{techStack.label}</Text>
                  {instructionsCopied && (
                    <Text variant="text-sm" color="tertiary" className="mt-bg-v2-xs">{INSTRUCTIONS_COPIED}</Text>
                  )}
                </div>
              ) : null}

              <Input
                id="client-name-clone"
                name="client-name-clone"
                label="Client Name"
                value={userInput.clientName || ''}
                onChange={e => setUserInput({ ...userInput, clientName: e.target.value })}
                placeholder="Enter client name"
                className="w-full"
              />
            </div>

            {error && <Text ref={errorRef} variant="text-sm" color="error" className="w-full">{error}</Text>}
          </div>

          {/* Last Section */}
          <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
            {/* The stack's name while the Custom option is chosen, in view however
                far the form scrolls. */}
            {stackNameInFooter && custom && <StackNameInFooter custom={custom} choice={choice} />}
            <Button
              variant="outline"
              label="Cancel"
              type="button"
              onClick={handleClose}
              disabled={loading}
              className={stackNameInFooter ? 'shrink-0' : undefined}
            />
            <Button
              variant="primary"
              type="submit"
              label={loading ? 'Cloning…' : 'Clone Project'}
              disabled={isSubmitDisabled}
              className={stackNameInFooter ? 'shrink-0' : undefined}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

